'use client';

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { Play, Square, ChevronDown, User, Mic, Loader2 } from 'lucide-react';
import type { TTSConfig } from '@/shared/types/audio';
import type { Language } from '../types';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Label } from '@/shared/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { cn } from '@/shared/utils/cn';
import { mediaSrc } from '@/contexts/media/utils/mediaSrc';

// ---------------------------------------------------------------------------
// Backward-compatible exports (used by StoryCreationForm & StoryEditor)
// ---------------------------------------------------------------------------

export const VOICE_PRESETS = {
  male: {
    voiceId: 'male',
    voiceName: 'male',
    stability: 0.5,
    similarityBoost: 0.75,
    style: 0,
    useSpeakerBoost: true,
  },
  female: {
    voiceId: 'female',
    voiceName: 'female',
    stability: 0.5,
    similarityBoost: 0.75,
    style: 0,
    useSpeakerBoost: true,
  },
} as const;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Voice entry returned by the familyCustomVoices GraphQL query / VoiceCatalogService */
export interface VoiceEntry {
  voiceId: string;
  name: string;
  previewUrl?: string | null;
  gender?: string | null;
  category?: string | null;
  description?: string | null;
  labels?: Record<string, string> | null;
  isCustom?: boolean;
  /** Language the voice was recorded in (for cloned voices) */
  language?: string | null;
}

export interface VoiceSelectorProps {
  /** Currently selected voice id */
  selectedVoiceId?: string;
  /** Callback when a voice is selected */
  onVoiceSelect?: (voiceId: string, voiceName: string) => void;
  /** Family id — when provided, custom (cloned) voices are included */
  familyId?: string;
  /** Optional language filter (not yet wired to server filtering) */
  language?: Language;
  /** Disable the selector */
  disabled?: boolean;
  /** Compact mode: no label, smaller trigger. Used inside toolbars. */
  compact?: boolean;
  /**
   * Pre-loaded voices to render instead of fetching from the server.
   * Useful in tests and storybook.
   */
  voices?: VoiceEntry[];

  // ---- Legacy props (backward compatibility with old API) ----

  /** @deprecated Use selectedVoiceId instead */
  selectedVoice?: TTSConfig | null;
  /** @deprecated Use onVoiceSelect instead */
  onSelect?: (voice: TTSConfig) => void;
  /** @deprecated No longer needed */
  autoSelect?: boolean;
}

// ---------------------------------------------------------------------------
// Hooks (server-action-backed React Query)
// ---------------------------------------------------------------------------

import { useVoicePresets as useVoicePresetsHook, useCustomVoices as useCustomVoicesHook } from '../hooks/useVoices';
import { useCustomVoicePreviewMutation } from '../hooks/useVoicePreviewMutation';

// ---------------------------------------------------------------------------
// Audio preview hook
// ---------------------------------------------------------------------------

function useAudioPreview() {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null);

  const play = useCallback((voiceId: string, previewUrl: string) => {
    // Stop any currently playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    const audio = new Audio(mediaSrc(previewUrl));
    audioRef.current = audio;
    setPlayingVoiceId(voiceId);

    audio.addEventListener('ended', () => {
      setPlayingVoiceId(null);
      audioRef.current = null;
    });

    audio.addEventListener('error', () => {
      setPlayingVoiceId(null);
      audioRef.current = null;
    });

    audio.play().catch(() => {
      setPlayingVoiceId(null);
      audioRef.current = null;
    });
  }, []);

  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingVoiceId(null);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  return { playingVoiceId, play, stop };
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface VoiceItemProps {
  voice: VoiceEntry;
  isSelected: boolean;
  isPlaying: boolean;
  isGeneratingPreview: boolean;
  onSelect: () => void;
  onPlayPreview: () => void;
  onStopPreview: () => void;
  t: (key: string) => string;
}

function VoiceItem({
  voice,
  isSelected,
  isPlaying,
  isGeneratingPreview,
  onSelect,
  onPlayPreview,
  onStopPreview,
  t,
}: VoiceItemProps) {
  const isCustom = voice.isCustom || voice.category === 'cloned';
  const canPlay = voice.previewUrl || isCustom; // Custom voices can generate preview on demand

  return (
    <div
      role="option"
      aria-selected={isSelected}
      data-testid={`voice-item-${voice.voiceId}`}
      className={cn(
        'flex items-center gap-3 px-3 py-2 min-h-12 cursor-pointer rounded-md transition-colors',
        isSelected
          ? 'bg-primary/10 border border-primary/30'
          : 'hover:bg-accent/50 border border-transparent',
      )}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      tabIndex={0}
    >
      {/* Voice icon */}
      <div className="flex-shrink-0">
        {isCustom ? (
          <Mic className="size-4 text-primary" />
        ) : (
          <User className="size-4 text-muted-foreground" />
        )}
      </div>

      {/* Voice info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium truncate">
            {isCustom
              ? <>
                  {voice.name}
                  {voice.language && (
                    <span className="text-muted-foreground font-normal"> ({voice.language.toUpperCase()})</span>
                  )}
                </>
              : voice.gender
                ? t(`gender.${voice.gender.toLowerCase()}`)
                : voice.name}
          </span>
          {isCustom && (
            <Badge
              variant="default"
              className="text-[10px] px-1.5 py-0"
              data-testid={`voice-badge-${voice.voiceId}`}
            >
              {t('categoryCustom')}
            </Badge>
          )}
        </div>
      </div>

      {/* Preview button */}
      {canPlay && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="flex-shrink-0 size-8"
          data-testid={`voice-preview-${voice.voiceId}`}
          disabled={isGeneratingPreview}
          onClick={(e) => {
            e.stopPropagation();
            if (isPlaying) {
              onStopPreview();
            } else {
              onPlayPreview();
            }
          }}
          aria-label={isPlaying ? t('stopPreview') : t('playPreview')}
        >
          {isGeneratingPreview ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : isPlaying ? (
            <Square className="size-3.5" />
          ) : (
            <Play className="size-3.5" />
          )}
        </Button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Legacy VoiceSelector (gender-only, used by old callers)
// ---------------------------------------------------------------------------

type LegacyVoiceGender = 'male' | 'female';

interface LegacyVoiceSelectorProps {
  selectedVoice: TTSConfig | null;
  onSelect: (voice: TTSConfig) => void;
  disabled: boolean;
  t: (key: string) => string;
}

function LegacyVoiceSelector({
  selectedVoice,
  onSelect,
  disabled,
  t,
}: LegacyVoiceSelectorProps) {
  const currentGender: LegacyVoiceGender | undefined = useMemo(() => {
    if (!selectedVoice) return undefined;
    if (selectedVoice.voiceName === 'male') return 'male';
    if (selectedVoice.voiceName === 'female') return 'female';
    return undefined;
  }, [selectedVoice]);

  const handleSelect = (gender: LegacyVoiceGender) => {
    onSelect(VOICE_PRESETS[gender]);
  };

  return (
    <div className="space-y-1.5" data-testid="voice-selector">
      <Label className="text-sm font-medium">{t('selectVoice')}</Label>
      <Select
        value={currentGender}
        onValueChange={handleSelect}
        disabled={disabled}
      >
        <SelectTrigger className="w-full" data-testid="select-voice">
          <SelectValue placeholder={t('chooseVoice')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="male">{t('maleVoice')}</SelectItem>
          <SelectItem value="female">{t('femaleVoice')}</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

// ---------------------------------------------------------------------------
// VoiceSelector component
// ---------------------------------------------------------------------------

/**
 * VoiceSelector — exported component
 *
 * Supports two calling conventions:
 *  - **New API**: `onVoiceSelect`, `selectedVoiceId`, `voices`, etc.
 *  - **Legacy API**: `onSelect`, `selectedVoice`, `autoSelect`
 *
 * The legacy API renders a simple male/female gender picker (backward compat).
 */
export function VoiceSelector(props: VoiceSelectorProps) {
  const {
    onSelect,
    onVoiceSelect,
    selectedVoice,
    autoSelect,
    disabled = false,
  } = props;

  // Detect legacy mode: caller passes onSelect but not onVoiceSelect
  const isLegacyMode = onSelect !== undefined && onVoiceSelect === undefined;

  if (isLegacyMode) {
    return (
      <LegacyVoiceSelectorWrapper
        selectedVoice={selectedVoice ?? null}
        onSelect={onSelect!}
        disabled={disabled}
        autoSelect={autoSelect}
      />
    );
  }

  return <NewVoiceSelector {...props} />;
}

// ---------------------------------------------------------------------------
// Legacy wrapper (its own component so hooks are unconditional)
// ---------------------------------------------------------------------------

function LegacyVoiceSelectorWrapper({
  selectedVoice,
  onSelect,
  disabled,
  autoSelect,
}: {
  selectedVoice: TTSConfig | null;
  onSelect: (voice: TTSConfig) => void;
  disabled: boolean;
  autoSelect?: boolean;
}) {
  const t = useTranslations('stories.audio.voiceSelector');

  useEffect(() => {
    if (autoSelect && !selectedVoice) {
      onSelect(VOICE_PRESETS.female);
    }
  }, [autoSelect, selectedVoice, onSelect]);

  return (
    <LegacyVoiceSelector
      selectedVoice={selectedVoice}
      onSelect={onSelect}
      disabled={disabled}
      t={t}
    />
  );
}

// ---------------------------------------------------------------------------
// New VoiceSelector (full-featured, its own component for clean hooks)
// ---------------------------------------------------------------------------

function NewVoiceSelector({
  selectedVoiceId,
  onVoiceSelect,
  familyId,
  language,
  disabled = false,
  compact = false,
  voices: externalVoices,
}: VoiceSelectorProps) {
  const t = useTranslations('stories.audio.voiceSelector');

  // State
  const [open, setOpen] = useState(false);
  const [localCustomVoices, setLocalCustomVoices] = useState<VoiceEntry[]>(
    externalVoices?.filter((v) => v.isCustom) ?? [],
  );

  const { playingVoiceId, play, stop } = useAudioPreview();

  // Server-action-backed queries (only used when no external voices provided)
  const lang = (language || 'ru') as Language;
  const presetsQuery = useVoicePresetsHook(lang);
  const customQuery = useCustomVoicesHook(familyId);
  const previewMutation = useCustomVoicePreviewMutation();

  // Determine data source: external voices or server queries
  const presetVoices = useMemo(() => {
    if (externalVoices) return externalVoices.filter((v) => !v.isCustom);
    return (presetsQuery.data ?? []) as VoiceEntry[];
  }, [externalVoices, presetsQuery.data]);

  const customVoices = useMemo(() => {
    // Use localCustomVoices for preview URL updates
    if (localCustomVoices.length > 0) return localCustomVoices;
    if (externalVoices) return externalVoices.filter((v) => v.isCustom);
    return (customQuery.data ?? []) as VoiceEntry[];
  }, [externalVoices, customQuery.data, localCustomVoices]);

  const isLoading = externalVoices ? false : (presetsQuery.isLoading || customQuery.isLoading);

  // Sync localCustomVoices when server data arrives
  useEffect(() => {
    if (!externalVoices && customQuery.data) {
      setLocalCustomVoices(customQuery.data as VoiceEntry[]);
    }
  }, [externalVoices, customQuery.data]);

  // Auto-select first female preset only when no voice is selected at all.
  // If selectedVoiceId is set but not found among loaded voices — don't override
  // (the voice may be a custom voice still loading, or a deleted voice).
  useEffect(() => {
    if (isLoading || !onVoiceSelect || presetVoices.length === 0) return;

    // If caller already has a voice selected, don't auto-select
    if (selectedVoiceId) return;

    const female = presetVoices.find((v) => v.gender?.toLowerCase() === 'female');
    const fallback = female ?? presetVoices[0];
    onVoiceSelect(fallback.voiceId, fallback.name);
  }, [isLoading, presetVoices.length, customVoices.length, selectedVoiceId]); // eslint-disable-line react-hooks/exhaustive-deps

  // All voices for lookup (presets + custom)
  const allVoices = useMemo(
    () => [...presetVoices, ...customVoices],
    [presetVoices, customVoices],
  );

  // Find selected voice for display
  // displayVoiceId stored in blocks can be "male"/"female" (preset name) or actual provider voiceId
  const selectedVoice = useMemo(
    () => allVoices.find((v) => v.voiceId === selectedVoiceId)
      ?? allVoices.find((v) => v.gender?.toLowerCase() === selectedVoiceId?.toLowerCase()),
    [allVoices, selectedVoiceId],
  );

  const handleSelect = useCallback(
    (voice: VoiceEntry) => {
      onVoiceSelect?.(voice.voiceId, voice.name);
      stop();
      setOpen(false);
    },
    [onVoiceSelect, stop],
  );

  // Handle play preview — generate on demand if missing for custom voices
  const handlePlayPreview = useCallback(
    async (voice: VoiceEntry) => {
      if (voice.previewUrl) {
        play(voice.voiceId, voice.previewUrl);
        return;
      }

      // Generate preview on demand for custom voices (async — Python worker)
      if (!voice.isCustom) return;

      const result = await previewMutation.mutateAsync(voice.voiceId);

      if (result.success && result.data?.jobId) {
        // Preview generation enqueued — result will arrive via SSE.
        // For now mark as generating; parent should poll/invalidate custom voices.
        // If jobId is 'existing', preview already exists (shouldn't reach here).
      }
    },
    [play, previewMutation],
  );

  const generatingPreviewForId = previewMutation.isPending ? previewMutation.variables : null;

  return (
    <div className={compact ? '' : 'space-y-1.5'} data-testid="voice-selector">
      {!compact && <Label className="text-sm font-medium">{t('selectVoice')}</Label>}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className={cn('w-full justify-between', compact && 'h-8 text-xs')}
            data-testid="voice-selector-trigger"
          >
            <span className="truncate">
              {selectedVoice
                ? selectedVoice.isCustom
                  ? selectedVoice.language
                    ? `${selectedVoice.name} (${selectedVoice.language.toUpperCase()})`
                    : selectedVoice.name
                  : selectedVoice.gender
                    ? t(`gender.${selectedVoice.gender.toLowerCase()}`)
                    : selectedVoice.name
                : t('chooseVoice')}
            </span>
            <ChevronDown className="ml-2 size-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>

        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-0"
          align="start"
          data-testid="voice-selector-dropdown"
        >
          {/* Voice list */}
          <div
            className="max-h-72 overflow-y-auto p-1"
            role="listbox"
            data-testid="voice-list"
          >
            {isLoading && (
              <div className="py-4 text-center text-sm text-muted-foreground" data-testid="voice-loading">
                {t('loading')}
              </div>
            )}

            {/* Standard voices (admin-configured male/female with preview) */}
            {!isLoading && presetVoices.length > 0 && (
              <>
                <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground" data-testid="voice-group-standard">
                  {t('groupStandard')}
                </div>
                {presetVoices.map((voice) => (
                  <VoiceItem
                    key={voice.voiceId}
                    voice={voice}
                    isSelected={voice.voiceId === selectedVoiceId}
                    isPlaying={playingVoiceId === voice.voiceId}
                    isGeneratingPreview={generatingPreviewForId === voice.voiceId}
                    onSelect={() => handleSelect(voice)}
                    onPlayPreview={() => handlePlayPreview(voice)}
                    onStopPreview={stop}
                    t={t}
                  />
                ))}
              </>
            )}

            {/* Custom/cloned voices */}
            {!isLoading && customVoices.length > 0 && (
              <>
                <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground" data-testid="voice-group-custom">
                  {t('groupCustom')}
                </div>
                {customVoices.map((voice) => (
                  <VoiceItem
                    key={voice.voiceId}
                    voice={voice}
                    isSelected={voice.voiceId === selectedVoiceId}
                    isPlaying={playingVoiceId === voice.voiceId}
                    isGeneratingPreview={generatingPreviewForId === voice.voiceId}
                    onSelect={() => handleSelect(voice)}
                    onPlayPreview={() => handlePlayPreview(voice)}
                    onStopPreview={stop}
                    t={t}
                  />
                ))}
              </>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
